import Marionette from "backbone.marionette";
import Radio from "backbone.radio";
import fineUploaderCore from "fine-uploader/lib/core";
import environment from "../../../common/environment";
import templates from "../../../utils/templates";
import simpleAlert from "../../../utils/alerts/simple";
import { errorParser } from "../../../utils";

import "../../../../../sass/chart-reviews-bulk-import.scss";

const globalChannel = Radio.channel("global");
const localChannel = Radio.channel("local");

const VIEW_DEFAULTS = {
  tagName: "form",
  template: templates.find("chart_reviews", "chart_bulk_import_form"),
};

// const UPLOADER_STATUS = fineUploaderCore.status;
const UPLOADER_DEFAULTS = {
  // debug: true,
  multiple: false,
  autoUpload: false,
  validation: {
    acceptFiles: "text/comma-separated-values, text/csv, application/csv",
  },
  request: {
    inputName: "file",
    filenameParam: "fileName",
    totalFileSizeName: "fileSize",
    uuidName: "uuid",
    requireSuccessJson: false,
  },
  retry: {
    enableAuto: false,
  },
  callbacks: {
    onError(id, name, errorResponse, xhr) {
      localChannel.trigger("uploader:error", { name, xhr });
    },
    // called once for each selected item
    onValidate(data) {
      localChannel.trigger("uploader:file-queued", data);
    },
    // called when the item has been selected and is a candidate for uploading
    onSubmit: (id, name) => {
      localChannel.trigger("uploader:file-submitted", { id, name });
    },
    // called when the item has been successfully submitted to the uploader
    onSubmitted: (id, name) => {
      localChannel.trigger("uploader:file-accepted", { id, name });
    },
    onUpload(id, name) {
      localChannel.trigger("uploader:start", { id, name });
    },
    // called before each automatic retry attempt for a failed item
    onAutoRetry(id, name, attempNumber) {
      localChannel.trigger("uploader:retry", { id, attempNumber });
    },
    // called during the upload, as it progresses, but only for the AJAX uploader
    onProgress(id, name, uploadedBytes, totalBytes) {
      const pct = Math.floor((uploadedBytes / totalBytes) * 100);
      localChannel.trigger("uploader:progress", { id, name, pct });
    },
    // called when the item has finished uploading
    onComplete(id, name) {
      localChannel.trigger("uploader:complete", { id, name });
    },
    // called when all submitted items have reached a point of termination
    onAllComplete(succeeded, failed) {
      localChannel.trigger("uploader:all-complete", { succeeded, failed });

      // reset upload queue
      this.reset();
    },
  },
};

class View extends Marionette.ItemView {
  constructor(options = {}) {
    Object.assign(options, VIEW_DEFAULTS);
    super(options);
  }

  ui() {
    return {
      importType: "input[name=type]",
      activeImportType: "input[name=type]:checked",
      instructionsLink: "a.instructions",
      uploadFileSelector: ".upload-file-selector",
      uploadFileDetails: ".upload-file-details",
      submitBtn: "button[type=submit]",
    };
  }

  events() {
    return {
      "change @ui.importType": "_onImportTypeChange",
      "click @ui.instructionsLink": "_onInstructionsLinkClick",
    };
  }

  initialize() {
    // add custom callbacks
    UPLOADER_DEFAULTS.callbacks = Object.assign(
      {},
      UPLOADER_DEFAULTS.callbacks,
      {
        // called when the item has been selected and is a candidate for uploading
        onSubmit: () => {
          // we only allow one upload at a time, so cancel previously
          // queued up files before queuing up a new one
          this.uploader.cancelAll();
        },
        // called when the item has been successfully submitted to the uploader
        onSubmitted: (id) => {
          const file = this.uploader.getUploads({ id });
          localChannel.trigger("uploader:file-accepted", file);
        },
      }
    );

    // setup listeners
    this.listenTo(localChannel, "uploader:error", this._onUploadError);
    this.listenTo(
      localChannel,
      "uploader:file-accepted",
      this._onFileQueuedForUpload
    );
    this.listenTo(localChannel, "uploader:start", this._onUploadStart);
    this.listenTo(localChannel, "uploader:progress", this._onUploadProgress);
    // this.listenTo(localChannel, "uploader:complete", this._onUploadComplete);
    this.listenTo(
      localChannel,
      "uploader:all-complete",
      this._onAllUploadsComplete
    );
  }

  onBeforeAttach() {
    // extend uploader configuration with runtime UI references
    let config = Object.assign({}, UPLOADER_DEFAULTS, {
      form: {
        element: this.$el.get(0),
      },
      button: this.ui.uploadFileSelector.get(0),
    });

    // attach instance of uploader to form
    this.uploader = new fineUploaderCore.FineUploaderBasic(config);
  }

  onAttach() {
    this._setUploaderCustomHeaders();

    this._setUploaderEndpoint(this.ui.activeImportType.val());
  }

  _getEndpoint(type) {
    return environment.getEndpoint("chart-reviews:bulk-import", {
      type,
    });
  }

  _setUploaderEndpoint(type) {
    const endpoint = environment.getEndpoint("chart-reviews:bulk-import", {
      type,
    });
    this.uploader.setEndpoint(endpoint);
  }

  _onImportTypeChange(e) {
    this._setUploaderEndpoint(e.target.value);
  }

  _onInstructionsLinkClick(e) {
    e.preventDefault();

    const { type } = e.target.dataset;
    const path = `/docs/bulk-import/${type}-instructions.pdf`;
    const windowName = `bulk-import-${type}-instructions`;
    globalChannel.trigger("file-link", path, windowName);
  }

  _setUploaderCustomHeaders() {
    const { token, shardId } = globalChannel.request("auth:credentials");
    const config = {
      authorization: `Bearer ${token}`,
      "shard-id": shardId,
    };

    this.uploader && this.uploader.setCustomHeaders(config);
  }

  _onUploadError(data) {
    const { name, xhr } = data;
    simpleAlert({
      title: `Your file, ${name}, could not be uploaded`,
      text: errorParser(xhr),
    }).then(() => {
      this._dismiss();
    });
  }

  _onFileQueuedForUpload(data) {
    const { name, size } = data;
    const formattedSize = this.uploader._formatSize(size);

    this.ui.uploadFileDetails.html(`${name} (${formattedSize})`);

    // unblock submit button
    this._toggleSubmitBtn(true);
  }

  _onUploadStart() {
    // dismisses the modal, but does not destroy it
    this._dismiss();
  }

  // _onUploadComplete(data) {
  // 	console.log("_onUploadComplete", data);
  // }

  _onUploadProgress(data) {
    const { name, pct } = data;
    const message = `Uploading ${name}... ${pct}%`;

    this._setStatus(message);
  }

  _onAllUploadsComplete(data) {
    const { failed } = data;

    // assume success
    let message = "Your upload completed successfully!";
    let type = "success";
    // unless the upload failed
    if (failed.length) {
      message = "Your upload has failed";
      type = "warning";
    }

    this._setStatus(message, type);
  }

  _toggleSubmitBtn(toggle) {
    this.ui.submitBtn.attr("disabled", !!!toggle);
  }

  _setStatus(message, type) {
    globalChannel.trigger("status-bar:update", message, type);
  }

  _dismiss() {
    this.triggerMethod("dismiss:modal");
  }
}

export { View as default };
